import type { ChangeEvent, MouseEvent, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

const CalendarIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor">
    <path
      opacity=".35"
      d="M18,21H6c-1.657,0-3-1.343-3-3V7h18v11C21,19.657,19.657,21,18,21z"
    />
    <path d="M18,3H6C4.343,3,3,4.343,3,6v1h18V6C21,4.343,19.657,3,18,3z" />
    <circle cx="14" cy="18" r="1" />
    <circle cx="10" cy="18" r="1" />
    <circle cx="6" cy="18" r="1" />
    <circle cx="6" cy="14" r="1" />
    <circle cx="10" cy="14" r="1" />
    <circle cx="14" cy="14" r="1" />
    <circle cx="18" cy="14" r="1" />
    <circle cx="18" cy="10" r="1" />
    <circle cx="14" cy="10" r="1" />
    <circle cx="10" cy="10" r="1" />
  </svg>
);

const PhoneIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor">
    <path
      opacity=".35"
      d="M16.48,3.086c-1.653,0.222-5.452,1.091-8.878,4.517s-4.295,7.224-4.517,8.878c-0.259,1.198,0.07,2.497,1.001,3.428c1.456,1.456,6.73-3.818,5.274-5.274l-0.929-0.929l5.274-5.274l0.929,0.929c1.456,1.456,6.73-3.818,5.274-5.274C18.977,3.155,17.678,2.826,16.48,3.086z"
    />
    <path d="M19.911,4.089l-5.273,5.273c1.457,1.453,3.815,1.453,5.271-0.003C21.363,7.905,21.364,5.546,19.911,4.089z" />
    <path d="M9.363,14.637L4.09,19.91c1.456,1.453,3.815,1.453,5.271-0.003C10.815,18.452,10.816,16.094,9.363,14.637z" />
  </svg>
);

const MailIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor">
    <path
      d="M2,17V7c0-1.657,1.343-3,3-3h14c1.657,0,3,1.343,3,3v10c0,1.657-1.343,3-3,3H5C3.343,20,2,18.657,2,17z"
      opacity=".35"
    />
    <path d="M22,7c0-0.478-0.122-0.924-0.321-1.325l-8.187,4.912c-0.919,0.552-2.063,0.552-2.983,0L2.321,5.675C2.122,6.076,2,6.522,2,7v1.981l6.964,4.179c0.937,0.562,1.986,0.843,3.036,0.843c1.049,0,2.1-0.281,3.035-0.843L22,8.981V7z" />
  </svg>
);

type RowProps = {
  id: string;
  label: string;
  value: string;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  autoComplete: string;
  type?: string;
  inputMode?: 'numeric';
  placeholder?: string;
  lead?: ReactNode;
  hint?: ReactNode;
  invalid: boolean;
  errorId: string;
  error?: string;
};

type MaskedRowProps = RowProps & {
  mask: string;
  display: string;
};

const ErrorHint = ({ id, error }: { id: string; error?: string }) =>
  error ? (
    <span className="rg-error-hint" id={id}>
      {error}
    </span>
  ) : null;

const TextRow = ({
  id,
  label,
  value,
  onChange,
  onFocus,
  onBlur,
  autoComplete,
  type,
  inputMode,
  placeholder,
  lead,
  hint,
  invalid,
  errorId,
  error,
}: RowProps) => (
  <div className="rg-field">
    <label className="rg-label" htmlFor={id}>
      {label}
    </label>
    <div className="rg-input-wrap">
      {lead && <span className="rg-input-lead">{lead}</span>}
      <input
        id={id}
        className={lead ? 'rg-input has-lead' : 'rg-input'}
        autoComplete={autoComplete}
        type={type}
        inputMode={inputMode}
        value={value}
        onChange={onChange}
        onFocus={onFocus}
        onBlur={onBlur}
        placeholder={placeholder}
        aria-invalid={invalid || undefined}
        aria-describedby={invalid ? errorId : undefined}
      />
    </div>
    {hint && <span className="rg-hint">{hint}</span>}
    <ErrorHint id={errorId} error={error} />
  </div>
);

const MaskedRow = ({
  id,
  label,
  value,
  onChange,
  onFocus,
  onBlur,
  autoComplete,
  type,
  inputMode,
  lead,
  hint,
  invalid,
  errorId,
  error,
  mask,
  display,
}: MaskedRowProps) => (
  <div className="rg-field">
    <label className="rg-label" htmlFor={id}>
      {label}
    </label>
    <div className="rg-input-wrap wreg-mask-wrap">
      {lead && <span className="rg-input-lead">{lead}</span>}
      <input
        id={id}
        className={lead ? 'rg-input has-lead' : 'rg-input'}
        autoComplete={autoComplete}
        type={type}
        inputMode={inputMode}
        value={value}
        onChange={onChange}
        onFocus={onFocus}
        onBlur={onBlur}
        placeholder=""
        aria-invalid={invalid || undefined}
        aria-describedby={invalid ? errorId : undefined}
      />
      <span className="wreg-mask-ghost" aria-hidden="true">
        <i>{display}</i>
        {mask.slice(display.length)}
      </span>
    </div>
    {hint && <span className="rg-hint">{hint}</span>}
    <ErrorHint id={errorId} error={error} />
  </div>
);

type LegalFooterProps = {
  onLegal: (document: 'terms_of_service' | 'privacy_policy') => (e: MouseEvent) => void;
};

const LegalFooter = ({ onLegal }: LegalFooterProps) => {
  const { t } = useTranslation();
  return (
    <p className="wreg-foot-legal-terms">
      {t('wreg.intake.legal.pre')}
      <a
        className="c-link"
        href={t('general.links.tos')}
        target="_blank"
        rel="noreferrer"
        onClick={onLegal('terms_of_service')}
      >
        {t('wreg.intake.legal.terms')}
      </a>
      {t('wreg.intake.legal.mid')}
      <a
        className="c-link"
        href={t('general.links.privacy-policy')}
        target="_blank"
        rel="noreferrer"
        onClick={onLegal('privacy_policy')}
      >
        {t('wreg.intake.legal.privacy')}
      </a>
      {t('wreg.intake.legal.post')}
    </p>
  );
};

export { TextRow, MaskedRow, LegalFooter, CalendarIcon, PhoneIcon, MailIcon };
